import { Injectable } from '@angular/core';
import { Observable, from } from 'rxjs';
import { UserModel } from '../../models/entities/user.model';
import { map } from 'rxjs/operators';
import { ContentfulApiService } from './contentful-api.service';
import { Entry } from 'contentful-management';
import { SubscriptionModel } from '../../models/entities/subscription.model';
import { UserService } from './user.service';
import { Constants } from '../../constants/constants';
import { CounterApiService as CounterApiService } from './counterApi.service';
import { Utils } from '../../utils/utils';

@Injectable({
    providedIn: "root",
})
export class SubscriptionService {
    private readonly ContentTypeId = "subscription";
    private readonly GroupChiefFieldId = "groupChief";
    private readonly GroupParticipantFieldId = "groupParticipant";
    constructor(
        private contentfulApiService: ContentfulApiService,
        private UserService: UserService,
        private counterApiService: CounterApiService
    ) {
    }

    getSubscriptionById(subscriptionId: string): Promise<SubscriptionModel | null> {
        return from(
            this.contentfulApiService.getEntries(this.ContentTypeId)
        ).pipe(
            map((items: Entry[]) => {
                const item = items!.find(
                    (item) => item.fields["id"]["en-US"] === subscriptionId
                );
                if (!item) return null;
                return this.mapDtoToModel(item);
            })
        ).toPromise();
    }

    searchByEmailGroup(email: string): Observable<SubscriptionModel | null> {
        return from(
            this.contentfulApiService.getEntries(this.ContentTypeId)
        ).pipe(
            map((items: Entry[]) => {
                const item = items!.find((item) => {
                    const groupChief = JSON.parse(
                        item.fields[this.GroupChiefFieldId]
                    ) as UserModel;
                    const groupParticipant = JSON.parse(
                        item.fields[this.GroupParticipantFieldId]
                    ) as UserModel;

                    return (
                        groupChief?.email.toLowerCase() ===
                            email.toLowerCase() ||
                        groupParticipant?.email.toLowerCase() ===
                            email.toLowerCase()
                    );
                });
                if (!item) return null;
                return this.mapDtoToModel(item);
            })
        );
    }

    getAll(): Observable<SubscriptionModel[]> {
        return from(
            this.contentfulApiService.getEntries(this.ContentTypeId)
        ).pipe(
            map((items: Entry[]) => {
                const users: SubscriptionModel[] = items.map((item) => {
                    return this.mapDtoToModel(item);
                });

                return users;
            })
        );
    }

    async create(data: SubscriptionModel): Promise<SubscriptionModel> {
        data.id = await this.counterApiService.generateSubscriptionId();
        const subscription = await this.CreateParticipants(data);
        const fields = this.mapModelToDto(subscription);

        const result = await this.contentfulApiService.createEntry(
            this.ContentTypeId,
            fields
        );
        return this.mapDtoToModel(result);
    }

    async CreateParticipants(data: SubscriptionModel): Promise<any> {
        await this.createAttachementAndUpdateParticipant(data);
        data.groupChief.subscriptionId = data.id;
        const groupChiefData = await this.UserService.create(data.groupChief!);
        data.groupChief!.contentfulId = groupChiefData.sys.id;

        for (let i = 0; i < data.groupParticipant.length; i++) {
            data.groupParticipant[i].subscriptionId = data.id;
            const p = await this.UserService.create(data.groupParticipant[i]);
            data.groupParticipant[i].contentfulId = p.sys.id;
        }

        return Promise.resolve(data);
    }

    async createAttachementAndUpdateParticipant(
        data: SubscriptionModel
    ): Promise<any> {
        try {
            const fileName =
                data.groupChief?.fullName.toLowerCase().replace(/ /g, "_") +
                "_diploma_" +
                Date.now().toString();
            if (data.groupChief.diplomaFile) {
                const assetId = await this.contentfulApiService.createAsset(
                    data.groupChief.diplomaFile,
                    fileName,
                    `DASP: ${data.groupChief.id}:${data.groupChief.email}. ${Constants.FileTypeDiploma}`
                );
                data.groupChief!.diplomaFileId = assetId;
            }

            for (let index = 0; index < data.groupParticipant.length; index++) {
                const participant = data.groupParticipant[index];
                if (participant.diplomaFile) {
                    const fileNameP =
                        participant?.fullName.toLowerCase().replace(/ /g, "_") +
                        "_diploma_" +
                        Date.now().toString();

                    const assetId = await this.contentfulApiService.createAsset(
                        participant.diplomaFile,
                        fileNameP,
                        `Uploaded via Design Atelier Students Prize: ${participant.id}:${participant.email}. ${Constants.FileTypeDiploma}`
                    );
                    data.groupParticipant[index]!.diplomaFileId = assetId;
                }
            }

            return data;
        } catch (error) {
            console.error(
                "Erreur lors de la création de l'attachement des participants :",
                error
            );
            throw error;
        }
    }

    update(data: SubscriptionModel): Observable<any> {
        const fields = {
            id: { "en-US": data.id },
            participationMode: { "en-US": data.participationMode },
            groupChief: { "en-US": JSON.stringify(data.groupChief) },
            groupParticipant: {
                "en-US": JSON.stringify(data.groupParticipant),
            },
            isSubscribedToDasp: { "en-US": data.isSubscribedToDasp },
            socialPlatform: { "en-US": data.socialPlatform },
            facebookAccount: { "en-US": data.facebookAccount },
            facebookLink: { "en-US": data.facebookLink },
            instagramAccount: { "en-US": data.instagramAccount },
            instagramLink: { "en-US": data.instagramLink },
            exactInformations: { "en-US": data.exactInformations },
            hasAcceptedTerms: { "en-US": data.hasAcceptedTerms },
            canUseContentForMarketing: {
                "en-US": data.canUseContentForMarketing,
            },
            confirmedRegistration: { "en-US": data.confirmedRegistration },
            env: Utils.getEnv()
        };

        return from(
            this.contentfulApiService.updateEntry(data.contentfulId, fields)
        );
    }

    mapDtoToModel(dto: any) {
        return {
            id: dto.fields["id"]["en-US"],
            participationMode: dto.fields["participationMode"]["en-US"],
            groupChief: dto.fields["groupChief"]["en-US"],
            groupParticipant: dto.fields["groupParticipant"]["en-US"],
            isSubscribedToDasp: dto.fields["isSubscribedToDasp"]["en-US"],
            socialPlatform: dto.fields["socialPlatform"]["en-US"],
            facebookAccount: dto.fields["facebookAccount"]["en-US"],
            facebookLink: dto.fields["facebookLink"]["en-US"],
            instagramAccount: dto.fields["instagramAccount"]["en-US"],
            instagramLink: dto.fields["instagramLink"]["en-US"],
            exactInformations: dto.fields["exactInformations"]["en-US"],
            hasAcceptedTerms: dto.fields["hasAcceptedTerms"]["en-US"],
            canUseContentForMarketing:
                dto.fields["canUseContentForMarketing"]["en-US"],
            confirmedRegistration: dto.fields["confirmedRegistration"]["en-US"],
            contentfulId: dto.sys.id,
        } as SubscriptionModel;
    }

    mapModelToDto(model: SubscriptionModel) {
        return {
            id: { "en-US": model.id },
            participationMode: { "en-US": model.participationMode },
            groupChief: { "en-US": model.groupChief },
            groupParticipant: {
                "en-US": model.groupParticipant,
            },
            isSubscribedToDasp: { "en-US": model.isSubscribedToDasp },
            socialPlatform: { "en-US": model.socialPlatform },
            facebookAccount: { "en-US": model.facebookAccount },
            facebookLink: { "en-US": model.facebookLink },
            instagramAccount: { "en-US": model.instagramAccount },
            instagramLink: { "en-US": model.instagramLink },
            exactInformations: { "en-US": model.exactInformations },
            hasAcceptedTerms: { "en-US": model.hasAcceptedTerms },
            canUseContentForMarketing: {
                "en-US": model.canUseContentForMarketing,
            },
            confirmedRegistration: {
                "en-US": model.confirmedRegistration,
            }
        };
    }
}
